import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Calendar, MessageSquare, Shield, ExternalLink,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse, ContractWithParties, Payment } from '@hirenfire/shared';

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contract, setContract] = useState<ContractWithParties | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [contractRes, paymentsRes] = await Promise.all([
          api.get<ApiResponse<ContractWithParties>>(`/contracts/${id}`),
          api.get<ApiResponse<Payment[]>>(`/contracts/${id}/payments`),
        ]);
        if (contractRes.data.data) setContract(contractRes.data.data);
        if (paymentsRes.data.data) setPayments(paymentsRes.data.data);
      } catch {
        navigate('/contracts');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [id, navigate]);

  if (isLoading || !contract) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Contracts
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{contract.job.title}</h1>
          <p className="text-gray-400 mt-1">
            {contract.agency.displayName} &rarr; {contract.worker.displayName}
          </p>
        </div>
        <Link to="/chat">
          <Button variant="secondary" leftIcon={<MessageSquare className="w-4 h-4" />}>
            Open Chat
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>Contract Terms</CardHeader>
            <CardBody>
              <p className="text-gray-300 whitespace-pre-wrap">{contract.terms}</p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>Payment History</CardHeader>
            <CardBody>
              {payments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No payments yet</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-dark-700/50"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{payment.type}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(payment.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-white">
                          ${payment.amount} {payment.currency}
                        </p>
                        <Badge
                          variant={
                            payment.status === 'CONFIRMED' ? 'success' :
                            payment.status === 'PENDING' ? 'warning' :
                            payment.status === 'FAILED' ? 'error' : 'default'
                          }
                        >
                          {payment.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardBody className="space-y-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-success-400" />
                <div>
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="font-semibold text-white">
                    ${contract.totalAmount} {contract.currency}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary-400" />
                <div>
                  <p className="text-xs text-gray-500">Start Date</p>
                  <p className="font-semibold text-white">
                    {new Date(contract.startDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {contract.endDate && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-warning-400" />
                  <div>
                    <p className="text-xs text-gray-500">End Date</p>
                    <p className="font-semibold text-white">
                      {new Date(contract.endDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-dark-600">
                <Badge
                  variant={
                    contract.status === 'ACTIVE' ? 'success' :
                    contract.status === 'COMPLETED' ? 'primary' :
                    contract.status === 'DISPUTED' ? 'error' : 'warning'
                  }
                >
                  {contract.status}
                </Badge>
              </div>
            </CardBody>
          </Card>

          {contract.escrowWallet && (
            <Card>
              <CardHeader>Escrow</CardHeader>
              <CardBody className="space-y-3">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-accent-400" />
                  <div>
                    <p className="text-xs text-gray-500">Escrow Balance</p>
                    <p className="font-semibold text-white">
                      ${contract.escrowWallet.balance}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Escrow Address</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-gray-400 font-mono bg-dark-700 px-2 py-1 rounded truncate flex-1">
                      {contract.escrowWallet.address}
                    </code>
                    <a
                      href="#"
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-400 hover:text-primary-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                <Badge variant={contract.escrowWallet.status === 'FUNDED' ? 'success' : 'warning'}>
                  {contract.escrowWallet.status}
                </Badge>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
